import { getJobSincro } from '_core/crud/sincro.crud';
import * as auth from '_core/store/auth.duck';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const POLLING_INTERVAL = 1000; // 1 segundos para que sea más visible
// const MAX_ATTEMPTS = 60; // Máximo 1 minuto de polling (60 * 1 segundos)

export function useSincroPolling() {
  const dispatch = useDispatch();
  const syncJob = useSelector(auth.selectors.getSyncJob);

  const [syncStatus, setSyncStatus] = useState({
    isLoading: false,
    isCompleted: false,
    error: null,
    progress: {},
    overallStatus: 'pending',
  });

  // Referencias para tracking de estado previo
  const previousProgressRef = useRef({});
  const startTimeRef = useRef(null);
  const attemptsRef = useRef(0);
  const intervalRef = useRef(null);
  const isPollingStoppedRef = useRef(false);
  const shouldShowComponentRef = useRef(false);

  useEffect(() => {
    if (syncJob?.isActive && syncJob?.jobId && syncJob?.token) {
      shouldShowComponentRef.current = true;
      startPolling();
    } else if (!syncJob?.isActive) {
      // Si el syncJob se limpia, resetear el flag para ocultar el componente
      shouldShowComponentRef.current = false;
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [syncJob?.isActive, syncJob?.jobId, syncJob?.token]);

  const startPolling = () => {
    if (!syncJob?.jobId || !syncJob?.token) return;

    setSyncStatus((prev) => ({ ...prev, isLoading: true, error: null }));
    attemptsRef.current = 0;
    previousProgressRef.current = {};
    startTimeRef.current = Date.now();
    isPollingStoppedRef.current = false;

    const poll = async () => {
      // Verificar si el polling ya se detuvo
      if (isPollingStoppedRef.current) {
        return;
      }

      // Verificar si hemos excedido el número máximo de intentos
      // if (attemptsRef.current >= MAX_ATTEMPTS) {
      //   clearInterval(intervalRef.current);
      //   isPollingStoppedRef.current = true;

      //   setSyncStatus((prev) => ({
      //     ...prev,
      //     isLoading: false,
      //     error: 'Timeout: Sync taking too long',
      //   }));

      //   return;
      // }

      try {
        attemptsRef.current++;

        const response = await getJobSincro(syncJob.jobId, syncJob.token);
        if (response?.data?.status) {
          const { status, children_jobs = [] } = response.data;
          let errors = { job_already_requested: 0, others: 0 };
          // Procesar el progreso de los jobs hijos
          const progress = {};
          children_jobs.forEach((job) => {
            if (job.status === 'failed' && job?.error === 'Job already requested') {
              errors['job_already_requested']++;
            } else if (job.status === 'failed' && job?.error !== 'Job already requested') {
              errors['others']++;
            }
            progress[job.type] = {
              status: job.status === 'failed' && job?.error === 'Job already requested' ? 'completed' : job.status,
              type: job.type,
              label: job.type,
            };
          });

          previousProgressRef.current = { ...progress };

          setSyncStatus({
            isLoading: true,
            isCompleted: status === 'completed',
            error: null,
            progress,
            overallStatus: status,
          });

          // Solo continuar polling si el status es 'processing'
          if (status === 'processing' || status === 'pending' || status === 'waiting') {
            // Continuar con el polling - no hacer nada, el setInterval seguirá ejecutándose
            return;
          }

          // Si está completado, detener el polling inmediatamente
          if (status === 'completed' || (errors['job_already_requested'] > 0 && status === 'failed' && errors['others'] === 0)) {
            clearInterval(intervalRef.current);
            isPollingStoppedRef.current = true;

            // Ejecutar requestUser inmediatamente
            dispatch(auth.actions.requestUser());

            // Actualizar estado visual inmediatamente
            setSyncStatus((prev) => ({
              ...prev,
              isLoading: false,
              error: null,
              overallStatus: 'completed',
            }));

            return;
          }

          // Si hay error, detener el polling inmediatamente
          if (status === 'error' || status === 'failed') {
            clearInterval(intervalRef.current);
            isPollingStoppedRef.current = true;

            // Actualizar estado visual inmediatamente
            setSyncStatus((prev) => ({
              ...prev,
              isLoading: false,
              error: 'Sync failed',
              overallStatus: status,
            }));

            return;
          }

          // Para cualquier otro status, detener el polling
          clearInterval(intervalRef.current);
          isPollingStoppedRef.current = true;

          setSyncStatus((prev) => ({
            ...prev,
            isLoading: false,
            error: `Unexpected status: ${status}`,
            overallStatus: status,
          }));
        }
      } catch (error) {
        console.error('Error polling sync status:', error);

        // Solo manejar el error si el polling no se ha detenido
        if (!isPollingStoppedRef.current) {
          clearInterval(intervalRef.current);
          isPollingStoppedRef.current = true;

          setSyncStatus((prev) => ({
            ...prev,
            isLoading: false,
            error: error.message || 'Network error',
          }));
        }
      }
    };

    // Ejecutar inmediatamente
    poll();

    // Configurar intervalo
    intervalRef.current = setInterval(poll, POLLING_INTERVAL);
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingStoppedRef.current = true;
    setSyncStatus((prev) => ({ ...prev, isLoading: false }));
  };

  // El componente debe mostrarse si hay un syncJob activo O si ya se inició y aún no se ha limpiado
  const isActive = syncJob?.isActive || shouldShowComponentRef.current;

  return {
    syncStatus,
    isActive,
  };
}
